/**
 * SC-158: the one place that decides a `<video>` has put pixels on screen.
 *
 * Binding a stream blanks an element until it decodes, so every cover over a
 * camera (the Join preview, the effects dialog, the handover underlay, the
 * tile's waiting cover) lifts on this rather than on the stream arriving. A
 * browser fix to the wait belongs here, once.
 *
 * Timeouts are not part of it: each caller bounds its own wait differently
 * (or not at all), and some count from a moment other than the call.
 */

type RequestsVideoFrames = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

/**
 * How long a preview waits for its element to paint before showing it anyway.
 *
 * Only a safety net: the frame callback normally fires within a frame or two of
 * the stream being bound. A camera that never delivers must not leave the
 * preview covered indefinitely.
 */
export const PREVIEW_PAINT_TIMEOUT_MS = 3000

/** Media events that stand in for a painted frame where there is no frame callback. */
export type VideoPaintFallbackEvent = 'loadeddata' | 'playing'

export interface WhenVideoPaintedOptions {
  /**
   * Presented frames to wait for. More than one where the first can be blank:
   * Safari's canvas `captureStream` can present the empty canvas first.
   */
  frames: number
  /**
   * Accepted instead where `requestVideoFrameCallback` is missing (Firefox).
   * They fire on decode, not paint, so they are weaker; the first one wins.
   */
  fallbackEvents: readonly VideoPaintFallbackEvent[]
}

/**
 * Calls `onPainted` once `video` has presented `frames` frames, or, without a
 * frame callback, on the first of `fallbackEvents`. `onPainted` runs at most
 * once, and never after the returned cancel, which withdraws a pending frame
 * request and the listeners.
 */
export function whenVideoPainted(
  video: HTMLVideoElement,
  { frames, fallbackEvents }: WhenVideoPaintedOptions,
  onPainted: () => void
): () => void {
  const element = video as RequestsVideoFrames
  let settled = false
  let frameHandle: number | null = null

  const painted = () => {
    if (settled) return
    settled = true
    onPainted()
  }

  // A frame callback fires on a frame that has been composited, the only
  // signal that means "not black any more".
  if (typeof element.requestVideoFrameCallback === 'function') {
    let presented = 0
    const onFrame = () => {
      if (settled) return
      presented += 1
      if (presented >= frames) {
        frameHandle = null
        painted()
        return
      }
      frameHandle = element.requestVideoFrameCallback!(onFrame)
    }
    frameHandle = element.requestVideoFrameCallback(onFrame)
  } else {
    for (const type of fallbackEvents) {
      element.addEventListener(type, painted, { once: true })
    }
  }

  return () => {
    settled = true
    if (frameHandle !== null && typeof element.cancelVideoFrameCallback === 'function') {
      element.cancelVideoFrameCallback(frameHandle)
    }
    for (const type of fallbackEvents) {
      element.removeEventListener(type, painted)
    }
  }
}
